import React, { useEffect, useState } from 'react';
import { FaStar, FaCodeBranch, FaBook, FaUsers, FaUserFriends, FaCode, FaGithub } from 'react-icons/fa';
import '../styles/GitHubStats.css';

const USERNAME = 'Atharv-Untwale';

const languageColors = {
  JavaScript: '#F7DF1E',
  TypeScript: '#3178C6',
  Python: '#3572A5',
  HTML: '#E34F26',
  CSS: '#563D7C',
  Java: '#B07219',
  'C++': '#F34B7D',
  Go: '#00ADD8',
  Rust: '#DEA584',
  Shell: '#89E051',
  Solidity: '#AA6746',
};

const GitHubStats = () => {
  const [userData, setUserData] = useState(null);
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartError, setChartError] = useState(false);

  useEffect(() => {
    const headers = { 'Accept': 'application/vnd.github.v3+json' };
    if (process.env.REACT_APP_GITHUB_TOKEN) {
      headers['Authorization'] = `token ${process.env.REACT_APP_GITHUB_TOKEN}`;
    }

    Promise.all([
      fetch(`https://api.github.com/users/${USERNAME}`, { headers }),
      fetch(`https://api.github.com/users/${USERNAME}/repos?per_page=100&sort=updated`, { headers })
    ])
      .then(([u, r]) => Promise.all([u.json(), r.json()]))
      .then(([user, repoData]) => {
        setUserData(user);
        setRepos(Array.isArray(repoData) ? repoData : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const totalStars = repos.reduce((a, r) => a + r.stargazers_count, 0);
  const totalForks = repos.reduce((a, r) => a + r.forks_count, 0);

  const languageMap = {};
  repos.forEach(r => {
    if (r.language) languageMap[r.language] = (languageMap[r.language] || 0) + 1;
  });
  const topLanguages = Object.entries(languageMap).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxLangCount = topLanguages[0]?.[1] || 1;

  const stats = userData ? [
    { icon: <FaBook />, number: userData.public_repos, label: 'Repositories' },
    { icon: <FaStar />, number: totalStars, label: 'Total Stars' },
    { icon: <FaCodeBranch />, number: totalForks, label: 'Total Forks' },
    { icon: <FaUsers />, number: userData.followers, label: 'Followers' },
    { icon: <FaUserFriends />, number: userData.following, label: 'Following' },
    { icon: <FaCode />, number: Object.keys(languageMap).length, label: 'Languages' },
  ] : [];

  if (loading) {
    return (
      <section className="github-stats">
        <div className="github-container">
          <h2 className="github-title">GitHub <span className="highlight-cyan">Activity</span></h2>
          <div className="github-loading">
            <div className="github-spinner" />
            <p>Fetching GitHub data...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="github" className="github-stats">
      <div className="github-container">
        <h2 className="github-title">
          GitHub <span className="highlight-cyan">Activity</span>
        </h2>
        <p className="github-subtitle">A live look at my coding activity and contributions.</p>

        <div className="github-grid">

          {/* Profile + Stats */}
          <div className="github-card profile-stats-card">
            {userData && (
              <div className="github-profile-row">
                <img src={userData.avatar_url} alt="GitHub Avatar" className="github-avatar" />
                <div className="github-profile-info">
                  <h3 className="github-name">{userData.name || USERNAME}</h3>
                  <p className="github-bio">{userData.bio || 'Full Stack Developer | Web3 Explorer'}</p>
                  <a href={`https://github.com/${USERNAME}`} target="_blank" rel="noopener noreferrer" className="github-profile-link">
                    <FaGithub /> View on GitHub
                  </a>
                </div>
              </div>
            )}
            <div className="github-stats-grid">
              {stats.map((s, i) => (
                <div key={i} className="github-stat-item">
                  <div className="github-stat-icon">{s.icon}</div>
                  <div className="github-stat-number">{s.number}</div>
                  <div className="github-stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Languages */}
          <div className="github-card languages-card">
            <h3 className="github-card-label">Top Languages</h3>
            {topLanguages.length > 0 ? (
              <div className="github-languages">
                {topLanguages.map(([lang, count]) => (
                  <div key={lang} className="github-lang-item">
                    <div className="github-lang-info">
                      <span className="github-lang-dot" style={{ background: languageColors[lang] || '#00E5FF' }} />
                      <span className="github-lang-name">{lang}</span>
                    </div>
                    <div className="github-lang-bar-wrap">
                      <div
                        className="github-lang-bar"
                        style={{
                          width: `${(count / maxLangCount) * 100}%`,
                          background: languageColors[lang] || '#00E5FF',
                        }}
                      />
                    </div>
                    <span className="github-lang-count">{count} repos</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="github-empty">No language data available</p>
            )}
          </div>

          {/* Contribution Chart */}
          {!chartError && (
            <div className="github-card wide">
              <h3 className="github-card-label">Contribution Graph</h3>
              <img
                src={`https://ghchart.rshah.org/00E5FF/${USERNAME}`}
                alt="GitHub Contribution Chart"
                className="github-chart"
                onError={() => setChartError(true)}
              />
            </div>
          )}

        </div>
      </div>
    </section>
  );
};

export default GitHubStats;